import { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from '@tanstack/react-query';
import SheetModal from '@/components/General/SheetModal';
import Input from '@/components/General/inputs/Input';
import InputSection from '@/components/General/inputs/InputSection';
import SelectInput from '@/components/General/inputs/SelectInput';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import Toggle from '@/components/General/inputs/Toggle';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import { getPeopleByIds } from '@/data/people';
import {
    FOLLOW_UP_STEP_ACTION_GROUPS,
    FOLLOW_UP_STEP_ASSIGNMENT_MODES,
    getStepAction,
    getStepActionID,
    stepActionIsHuman,
} from '@/utils/constants/followUp';
import { DEFAULT_STEP_EXPIRY_DAYS } from '@/utils/followUpPaths';

const digitsOnly = (text) => `${text ?? ''}`.replace(/[^0-9]/g, '');

/**
 * StepEditorSheet — the compact per-step editor for the flat Path builder
 * (§25.5). One sheet carries only the fields its chosen action needs:
 * action, wait days, action details (template/message/instructions), and —
 * for human actions only — assignment + time limit. Automatic sends never
 * show assignment or deadline controls, and a volunteer action is never
 * presented as an automatic send.
 */
const StepEditorSheet = ({ isOpen, onClose, step, index = 0, onSave }) => {
    const isEdit = !!step;
    const [actionID, setActionID] = useState(null);
    const [label, setLabel] = useState('');
    const [instructions, setInstructions] = useState('');
    const [waitDays, setWaitDays] = useState('0');
    const [noLimit, setNoLimit] = useState(false);
    const [expiryDays, setExpiryDays] = useState(`${DEFAULT_STEP_EXPIRY_DAYS}`);
    const [emailTemplateID, setEmailTemplateID] = useState(null);
    const [message, setMessage] = useState('');
    const [assignMode, setAssignMode] = useState('PRIMARY_CONNECTOR');
    const [assigneeObj, setAssigneeObj] = useState(null);
    const [poolObjs, setPoolObjs] = useState([]);

    const action = getStepAction(actionID);
    const actionType = action?.actionType || null;
    const human = stepActionIsHuman(actionType);

    const { data: emailTemplates = [] } = Query.emailTemplates.all({
        options: { enabled: isOpen && actionType === 'SEND_EMAIL' },
    });
    const templateOptions = useMemo(
        () => (emailTemplates || []).filter((t) => t?.id).map((t) => ({ value: t.id, label: t.name || 'Email template' })),
        [emailTemplates]
    );

    // Resolve stored person ids to picker objects (assignee + pool).
    const poolMemberIDs = useMemo(() => (step?.config?.assignment?.poolMemberIDs || []).filter(Boolean), [step]);
    const { data: poolPeople = [] } = useQuery({
        queryKey: ['people', 'byIds', [...poolMemberIDs].sort().join(',')],
        queryFn: () => getPeopleByIds(poolMemberIDs, ['id', 'firstName', 'lastName']),
        enabled: isOpen && poolMemberIDs.length > 0,
        staleTime: 1000 * 60 * 5,
    });
    const { data: assigneeDetail } = Query.people.basicById({
        id: step?.config?.assignment?.personID,
        options: { enabled: isOpen && !!step?.config?.assignment?.personID && !assigneeObj },
    });

    useEffect(() => {
        if (!isOpen) return;
        setActionID(getStepActionID(step) || null);
        setLabel(step?.label || '');
        setInstructions(step?.instructions || '');
        setWaitDays(step?.waitDays != null ? `${step.waitDays}` : '0');
        const expires = step?.expiresAfterDays;
        setNoLimit(expires === null || expires === undefined);
        setExpiryDays(expires != null ? `${expires}` : `${DEFAULT_STEP_EXPIRY_DAYS}`);
        setEmailTemplateID(step?.config?.emailTemplateID || null);
        setMessage(step?.config?.message || '');
        setAssignMode(step?.config?.assignment?.mode || 'PRIMARY_CONNECTOR');
        setAssigneeObj(null);
        setPoolObjs([]);
    }, [isOpen, step]);

    useEffect(() => {
        if (isOpen && assigneeDetail?.id && !assigneeObj) setAssigneeObj(assigneeDetail);
    }, [isOpen, assigneeDetail, assigneeObj]);
    useEffect(() => {
        if (isOpen && poolObjs.length === 0 && poolPeople?.length) {
            setPoolObjs(poolMemberIDs.map((id) => poolPeople.find((p) => p.id === id)).filter(Boolean));
        }
    }, [isOpen, poolPeople, poolObjs.length, poolMemberIDs]);

    const handleActionPick = (nextID) => {
        const next = getStepActionID({ actionType: getStepAction(nextID)?.actionType, config: { channel: getStepAction(nextID)?.channel } });
        if (!next || next === actionID) return;
        setActionID(nextID);
        // Reset action-specific config so a stale template/message/channel
        // can't leak into the published payload.
        setEmailTemplateID(null);
        setMessage('');
        setAssignMode('PRIMARY_CONNECTOR');
        setAssigneeObj(null);
        setPoolObjs([]);
    };

    const poolMemberIDsLive = poolObjs.map((p) => p.id).filter(Boolean);
    const parsedWait = digitsOnly(waitDays);
    const parsedExpiry = digitsOnly(expiryDays);

    const valid = !!actionType
        && (actionType !== 'SEND_EMAIL' || !!emailTemplateID)
        && (actionType !== 'SEND_SMS' || !!message.trim())
        && (!human
            || ((assignMode !== 'SPECIFIC' || !!(assigneeObj?.id))
                && (assignMode !== 'POOL' || poolMemberIDsLive.length > 0)
                && (noLimit || (parsedExpiry && Number(parsedExpiry) > 0))));

    const handleSave = () => {
        if (!valid) return;
        const nextActionType = actionType;
        const config = {};
        if (nextActionType === 'SEND_EMAIL') {
            config.emailTemplateID = emailTemplateID;
        } else if (nextActionType === 'SEND_SMS') {
            config.message = message.trim();
        } else if (nextActionType === 'OUTREACH' || nextActionType === 'TASK') {
            if (nextActionType === 'OUTREACH') config.channel = action.channel;
            config.assignment = { mode: assignMode };
            if (assignMode === 'SPECIFIC') config.assignment.personID = assigneeObj?.id;
            if (assignMode === 'POOL') config.assignment.poolMemberIDs = poolMemberIDsLive;
        }
        onSave?.({
            stepID: step?.stepID,
            actionType: nextActionType,
            label: label.trim(),
            instructions: instructions.trim(),
            waitDays: parsedWait ? Number(parsedWait) : 0,
            expiresAfterDays: human ? (noLimit ? null : Number(parsedExpiry)) : null,
            config,
            // Display-only pool member objects ride along for the editor row;
            // the payload builder drops them.
            poolMembers: poolObjs,
        });
        onClose?.();
    };

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? 'Edit Step' : 'New Step'}
            stayOpenOnBlur
            snapPoints={[560, 780]}
            cancelText="Cancel"
            confirmText={isEdit ? 'Save Step' : 'Add Step'}
            onConfirm={valid ? handleSave : null}
        >
            <ScrollView keyboardShouldPersistTaps="handled" style={styles.scroll}>
                <InputSection>
                    <Text style={styles.label}>Action <Text style={styles.required}>*</Text></Text>
                    {FOLLOW_UP_STEP_ACTION_GROUPS.map((group) => (
                        <View key={group.label} style={styles.actionGroup}>
                            <Text style={styles.groupLabel}>{group.label}</Text>
                            <View style={styles.chipRow}>
                                {group.options.map((option) => {
                                    const selected = actionID === option.actionID;
                                    return (
                                        <Pressable
                                            key={option.actionID}
                                            testID={`step-action-${option.actionID}`}
                                            accessibilityRole="button"
                                            accessibilityState={{ selected }}
                                            onPress={() => handleActionPick(option.actionID)}
                                            style={[styles.actionChip, selected && styles.actionChipSelected]}
                                        >
                                            <Unicon name={option.icon} size={14} colorVariant={selected ? 'primary' : 'textSecondary'} />
                                            <Text style={[styles.actionChipText, selected && styles.actionChipTextSelected]}>
                                                {option.label}
                                            </Text>
                                        </Pressable>
                                    );
                                })}
                            </View>
                        </View>
                    ))}
                    {!!action?.description && <Text style={styles.hint}>{action.description}</Text>}

                    <Input
                        title="Step label (optional)"
                        placeholder={action?.label || 'Short name for this step'}
                        helpText="A generated name is used when this is blank."
                        value={label}
                        maxLength={120}
                        onUpdate={setLabel}
                        testID="step-editor-label"
                    />

                    <Input
                        title={`Wait (days) — ${index === 0 ? 'after enrollment' : 'after previous step'}`}
                        helpText={index === 0
                            ? 'Days to wait after the person starts this Path.'
                            : 'Days to wait after the previous step finishes.'}
                        value={waitDays}
                        maxLength={4}
                        onUpdate={setWaitDays}
                        testID="step-editor-wait-days"
                    />

                    {actionType === 'SEND_EMAIL' && (
                        <SelectInput
                            title="Email template"
                            placeholder="Select a template"
                            value={emailTemplateID}
                            options={templateOptions}
                            onUpdate={(v) => setEmailTemplateID((typeof v === 'object' ? v?.value : v) || null)}
                            testID="step-editor-email-template"
                        />
                    )}

                    {actionType === 'SEND_SMS' && (
                        <Input
                            title="Message text"
                            placeholder="Write the text to send..."
                            value={message}
                            multiline
                            required
                            onUpdate={setMessage}
                            testID="step-editor-message"
                        />
                    )}

                    {human && (
                        <Input
                            title="Instructions (optional)"
                            placeholder="What the assignee should know..."
                            value={instructions}
                            multiline
                            onUpdate={setInstructions}
                            testID="step-editor-instructions"
                        />
                    )}

                    {human && (
                        <>
                            <SelectInput
                                title="Assign to"
                                value={assignMode}
                                options={FOLLOW_UP_STEP_ASSIGNMENT_MODES.map((m) => ({ value: m.value, label: m.label, subtitle: m.description }))}
                                hideClearButton
                                onUpdate={(v) => setAssignMode((typeof v === 'object' ? v?.value : v) || 'PRIMARY_CONNECTOR')}
                                testID="step-editor-assignment"
                            />
                            {assignMode === 'SPECIFIC' && (
                                <SheetSelector
                                    type="people"
                                    title="Assignee"
                                    placeholder="Select a person"
                                    value={assigneeObj}
                                    onChange={(p) => setAssigneeObj(p && !Array.isArray(p) ? p : null)}
                                    testID="step-editor-assignee"
                                />
                            )}
                            {assignMode === 'POOL' && (
                                <SheetSelector
                                    type="people"
                                    title="Pool members"
                                    multiple
                                    placeholder="Select pool members"
                                    value={poolObjs}
                                    onChange={(people) => setPoolObjs((people || []).filter(Boolean))}
                                    testID="step-editor-pool"
                                />
                            )}
                            <View style={styles.toggleRow}>
                                <Text style={styles.toggleLabel}>No time limit</Text>
                                <Toggle
                                    testID="step-editor-no-limit"
                                    value={noLimit}
                                    onValueChange={setNoLimit}
                                />
                            </View>
                            {!noLimit && (
                                <Input
                                    title="Allow days to complete"
                                    helpText="The step expires when this runs out and the Path continues."
                                    value={expiryDays}
                                    maxLength={4}
                                    onUpdate={setExpiryDays}
                                    testID="step-editor-expiry-days"
                                />
                            )}
                        </>
                    )}
                </InputSection>
            </ScrollView>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    scroll: { flexGrow: 0 },
    label: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        marginTop: 4,
        marginBottom: 8,
    },
    required: { color: colors.error },
    actionGroup: { marginBottom: 10 },
    groupLabel: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.textTertiary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 6,
    },
    chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    actionChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 16,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        _web: { cursor: 'pointer' },
    },
    actionChipSelected: {
        backgroundColor: colors.primaryContainer,
        borderColor: colors.primary,
    },
    actionChipText: {
        fontSize: 13,
        color: colors.textSecondary,
        fontFamily: fonts.medium,
    },
    actionChipTextSelected: {
        color: colors.primary,
        fontFamily: fonts.bold,
    },
    hint: {
        fontSize: 12,
        color: colors.textSecondary,
        fontStyle: 'italic',
        marginTop: 2,
        marginBottom: 4,
    },
    toggleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 8,
    },
    toggleLabel: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.text,
        flex: 1,
        marginRight: 12,
    },
}));

export default StepEditorSheet;
